import { useMemo } from 'react';
import qrcode from 'qrcode-generator';

/**
 * Render a string as a QR code, as inline SVG.
 *
 * WHY A DEPENDENCY IS OK HERE, given `api/src/shared/utils/totp.js` deliberately
 * hand-rolls TOTP to keep dependencies out of the auth path: that rule is about
 * code which DECIDES whether someone is authenticated. This draws a string the
 * server already produced and already displays as text. It computes nothing
 * secret, verifies nothing, and if it fails the caller still shows the key.
 * Different risk profile, not a loophole.
 *
 * `qrcode-generator` has zero transitive dependencies.
 *
 * SVG rather than canvas, deliberately:
 *   - no toDataURL, so no `img-src data:` needed if a CSP ever lands
 *   - crisp at any size and in print, with no devicePixelRatio handling
 *   - the modules are real DOM, so the image survives a re-render intact
 *
 * Error correction is 'M' (~15%). 'L' would fit more data but a TOTP otpauth://
 * URI is short, and the margin is worth more than the capacity here — this gets
 * scanned off a screen, sometimes at an angle.
 */
export default function QrCode({
  value,
  size = 168,
  title = 'QR code',
  className = '',
}) {
  const path = useMemo(() => {
    if (!value) return null;
    try {
      // 0 = let the library pick the smallest type version that fits.
      const qr = qrcode(0, 'M');
      qr.addData(value);
      qr.make();

      const count = qr.getModuleCount();
      const parts = [];
      for (let row = 0; row < count; row += 1) {
        for (let col = 0; col < count; col += 1) {
          if (qr.isDark(row, col)) {
            // One path command per dark module. A single <path> keeps the DOM
            // to one node rather than hundreds of <rect>s.
            parts.push(`M${col} ${row}h1v1h-1z`);
          }
        }
      }
      return { d: parts.join(''), count };
    } catch {
      // A QR that cannot be built must not take the dialog down — the setup key
      // beside it is the fallback, and it is always rendered.
      return null;
    }
  }, [value]);

  if (!path) return null;

  // The quiet zone is required for reliable scanning; 4 modules is the spec
  // minimum. Built into the viewBox so no external padding can remove it.
  const quiet = 4;
  const extent = path.count + quiet * 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${extent} ${extent}`}
      className={className}
      role="img"
      aria-label={title}
      shapeRendering="crispEdges"
    >
      {/* Always a true white background, never the theme surface: scanners need
          the light modules light, and this dialog renders on near-black. */}
      <rect width={extent} height={extent} fill="#ffffff" />
      <g transform={`translate(${quiet} ${quiet})`}>
        <path d={path.d} fill="#000000" />
      </g>
    </svg>
  );
}
